import { useMemo } from "react";

import {
  BanknotesIcon,
  CameraIcon,
  ChartBarIcon,
  ClockIcon,
  EyeIcon,
  FireIcon,
  FlagIcon,
  MegaphoneIcon,
  SparklesIcon,
  TagIcon,
} from "@heroicons/react/24/outline";

import useListingStats from "../../../data/useListingStats";
import PriceFormat from "../../../utils/priceFormat";
import classNames from "../../../utils/classNames";
import { EnrichedListing, MIN_RECOMMENDED_IMAGES } from "./marketplaceInsightsHelpers";

interface Props {
  listings: EnrichedListing[];
}

/** Visual tones for each stat tile. Gradient + ring + icon colour
 *  are driven off a single key so the whole card reads cohesively. */
const tones = {
  sky: {
    ring: "ring-sky-200/70",
    bg: "from-sky-50/90 to-white",
    icon: "bg-sky-100 text-sky-700 ring-sky-200",
    value: "text-sky-900",
    accent: "text-sky-700",
  },
  violet: {
    ring: "ring-violet-200/70",
    bg: "from-violet-50/90 to-white",
    icon: "bg-violet-100 text-violet-700 ring-violet-200",
    value: "text-violet-900",
    accent: "text-violet-700",
  },
  indigo: {
    ring: "ring-indigo-200/70",
    bg: "from-indigo-50/90 to-white",
    icon: "bg-indigo-100 text-indigo-700 ring-indigo-200",
    value: "text-indigo-900",
    accent: "text-indigo-700",
  },
  emerald: {
    ring: "ring-emerald-200/70",
    bg: "from-emerald-50/90 to-white",
    icon: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    value: "text-emerald-900",
    accent: "text-emerald-700",
  },
  orange: {
    ring: "ring-orange-200/70",
    bg: "from-orange-50/90 to-white",
    icon: "bg-orange-100 text-orange-700 ring-orange-200",
    value: "text-orange-900",
    accent: "text-orange-700",
  },
  amber: {
    ring: "ring-amber-200/70",
    bg: "from-amber-50/90 to-white",
    icon: "bg-amber-100 text-amber-700 ring-amber-200",
    value: "text-amber-900",
    accent: "text-amber-700",
  },
  rose: {
    ring: "ring-rose-200/70",
    bg: "from-rose-50/90 to-white",
    icon: "bg-rose-100 text-rose-700 ring-rose-200",
    value: "text-rose-900",
    accent: "text-rose-700",
  },
  slate: {
    ring: "ring-slate-200/70",
    bg: "from-slate-50/90 to-white",
    icon: "bg-slate-100 text-slate-700 ring-slate-200",
    value: "text-slate-900",
    accent: "text-slate-700",
  },
} as const;

type ToneKey = keyof typeof tones;

interface StatTileProps {
  title: string;
  value: string;
  sub?: string;
  trend?: string;
  tone: ToneKey;
  icon: React.ElementType;
  span?: "sm" | "md" | "lg";
}

function StatTile({ title, value, sub, trend, tone, icon: Icon, span = "sm" }: StatTileProps) {
  const t = tones[tone];
  return (
    <div
      className={classNames(
        "group relative overflow-hidden rounded-xl border border-slate-200/80 bg-gradient-to-b p-3 shadow-sm ring-1 ring-inset transition-shadow hover:shadow-md",
        t.bg,
        t.ring,
        span === "lg" && "sm:col-span-2 xl:col-span-2",
        span === "md" && "sm:col-span-2 xl:col-span-1"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {title}
          </div>
          <div className={classNames("mt-1 truncate text-2xl font-bold tabular-nums leading-tight", t.value)}>
            {value}
          </div>
          {sub && (
            <div className={classNames("mt-0.5 truncate text-xs font-medium", t.accent)}>
              {sub}
            </div>
          )}
        </div>
        <div
          className={classNames(
            "inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg ring-1 ring-inset",
            t.icon
          )}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {trend && (
        <div className="mt-2 text-[11px] font-medium text-slate-500">{trend}</div>
      )}
    </div>
  );
}

export default function MarketplaceInsightsStats({ listings }: Props) {
  const { listingStats } = useListingStats();
  const stats = listingStats.data?.data;

  const derived = useMemo(() => {
    if (listings.length === 0) {
      return {
        totalViews30d: 0,
        avgViews30d: 0,
        hotCount: 0,
        staleCount: 0,
        totalWatchers: 0,
        sampleAvgAge: 0,
        sampleTotalValue: 0,
        underImagedCount: 0,
      };
    }
    const totalViews30d = listings.reduce((sum, l) => sum + l.views30d, 0);
    const avgViews30d = Math.round(totalViews30d / listings.length);
    const sorted = [...listings].sort((a, b) => b.views30d - a.views30d);
    const top10Cutoff = sorted[Math.max(0, Math.floor(sorted.length * 0.1) - 1)]?.views30d ?? 0;
    const hotCount = listings.filter((l) => l.views30d >= Math.max(20, top10Cutoff)).length;
    const staleCount = listings.filter((l) => l.daysListed >= 90).length;
    const totalWatchers = listings.reduce((s, l) => s + l.watchers, 0);
    const sampleAvgAge = Math.round(
      listings.reduce((s, l) => s + l.daysListed, 0) / listings.length
    );
    const sampleTotalValue = listings.reduce((s, l) => s + l.price, 0);
    const underImagedCount = listings.filter(
      (l) => l.imageCount < MIN_RECOMMENDED_IMAGES
    ).length;
    return {
      totalViews30d,
      avgViews30d,
      hotCount,
      staleCount,
      totalWatchers,
      sampleAvgAge,
      sampleTotalValue,
      underImagedCount,
    };
  }, [listings]);

  const totalActive = stats?.totalActive ?? listings.length;
  const averageAge = stats ? Math.round(stats.averageAge) : derived.sampleAvgAge;
  const over30 = stats?.over30DaysOld ?? 0;
  const over90 = stats?.over90DaysOld ?? derived.staleCount;
  const averagePrice = stats?.averagePrice ?? null;
  const totalPrice = stats?.totalPrice ?? derived.sampleTotalValue;

  const stalePct = totalActive > 0 ? Math.round((over90 / totalActive) * 100) : 0;
  const hotPct = listings.length > 0
    ? Math.round((derived.hotCount / listings.length) * 100)
    : 0;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white/60 p-4 shadow-sm backdrop-blur">
      <div className="mb-3 flex items-end justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
            <SparklesIcon className="h-4 w-4 text-primary-500" />
            Marketplace health
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Live eBay &amp; Shopify performance at a glance — click through to
            dig into individual listings below.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile
          title="Live listings"
          value={totalActive.toLocaleString()}
          sub={`${derived.hotCount.toLocaleString()} hot · ${hotPct}%`}
          tone="sky"
          icon={MegaphoneIcon}
        />
        <StatTile
          title="Total list value"
          value={PriceFormat(totalPrice)}
          sub={averagePrice !== null ? `${PriceFormat(averagePrice)} avg` : undefined}
          tone="emerald"
          icon={BanknotesIcon}
        />
        <StatTile
          title="Views · 30d"
          value={derived.totalViews30d.toLocaleString()}
          sub={`${derived.avgViews30d.toLocaleString()} avg / listing`}
          tone="violet"
          icon={EyeIcon}
        />
        <StatTile
          title="Watchers"
          value={derived.totalWatchers.toLocaleString()}
          sub="Across live listings"
          tone="indigo"
          icon={ChartBarIcon}
        />
        <StatTile
          title="Hot listings"
          value={derived.hotCount.toLocaleString()}
          sub="Top 10% by views"
          tone="orange"
          icon={FireIcon}
        />
        <StatTile
          title="Avg age"
          value={`${averageAge}d`}
          sub="Days on marketplace"
          tone="amber"
          icon={ClockIcon}
        />
        <StatTile
          title="Over 30 days"
          value={over30.toLocaleString()}
          sub={totalActive > 0 ? `${Math.round((over30 / totalActive) * 100)}% of listings` : undefined}
          tone="amber"
          icon={TagIcon}
        />
        <StatTile
          title="Stale · 90d+"
          value={over90.toLocaleString()}
          sub={`${stalePct}% of listings`}
          tone="rose"
          icon={FlagIcon}
        />
        <StatTile
          title={`< ${MIN_RECOMMENDED_IMAGES} images`}
          value={derived.underImagedCount.toLocaleString()}
          sub={
            listings.length > 0
              ? `${Math.round((derived.underImagedCount / listings.length) * 100)}% of sampled listings`
              : "Add photos to lift rankings"
          }
          tone="rose"
          icon={CameraIcon}
        />
      </div>
    </section>
  );
}
